// src/ui/gate/useGateAuth.ts — top-level bootstrap hook (UI-09, D-01).
// Game path (default): probes /api/content; POSTs { password }.
// Admin path (opts.role='admin'): probes /api/admin/content/history; POSTs
// { password, role: 'admin' }.
//
// Both paths share the same stage machine + D-03 error copy; the role switch
// is the minimal change required to acquire hunter-admin-session via the same
// GateScreen UX. P5 Plan 05-11 gap closure — implements the 05-08 TODO and
// unblocks UAT Test 2 (see .planning/debug/admin-publish-401.md).

import { useCallback, useEffect, useRef, useState } from 'react';

export type AppStage = 'loading' | 'gate' | 'splash' | 'game';

export interface UseGateAuthOptions {
  /** When 'admin', the bootstrap probe + auth POST target the admin cookie. */
  role?: 'admin';
}

interface UseGateAuthReturn {
  stage: AppStage;
  error: string | null;
  login(password: string): Promise<void>;
  finishSplash(): void;
}

const GAME_PROBE_URL = '/api/content';
const ADMIN_PROBE_URL = '/api/admin/content/history';
const AUTH_URL = '/api/auth';

/**
 * Top-level bootstrap hook — UI-09, D-01.
 *
 * Stage machine:
 *   loading  → initial probe to probeUrl (same-origin, credentials included)
 *     ├── 200 → splash → game (auto-advance after 1.2s for game; admin stays in splash)
 *     └── 401 → gate
 *   gate     → login(password) POSTs /api/auth (with role in body iff admin)
 *     ├── 200 → splash → game
 *     └── 401 → stays in gate + sets error
 *
 * Admin vs game split:
 *   - Game path (default): probe /api/content, POST body { password }
 *   - Admin path (role:'admin'): probe /api/admin/content/history, POST body { password, role:'admin' }
 *     Backend issues the cookie variant based on the role key (functions/api/auth.ts).
 */
export function useGateAuth(options?: UseGateAuthOptions): UseGateAuthReturn {
  const role = options?.role;
  const isAdmin = role === 'admin';
  const probeUrl = isAdmin ? ADMIN_PROBE_URL : GAME_PROBE_URL;

  const [stage, setStage] = useState<AppStage>('loading');
  const [error, setError] = useState<string | null>(null);
  const probed = useRef(false);

  // Bootstrap: ping the probe endpoint — 200 means valid session cookie.
  useEffect(() => {
    if (probed.current) return;
    probed.current = true;
    void (async (): Promise<void> => {
      try {
        const res = await fetch(probeUrl, {
          credentials: 'include',
        });
        if (res.ok) {
          setStage('splash');
        } else {
          setStage('gate');
        }
      } catch {
        // Offline: assume gate needed. SW NetworkFirst may serve a cached
        // response on warm cache (game path only), which still satisfies `ok`.
        setStage('gate');
      }
    })();
  }, [probeUrl]);

  const login = useCallback(
    async (password: string): Promise<void> => {
      setError(null);
      try {
        // Construct body with role only when admin — the backend accepts
        // missing-role as game, but existing test suites assert on the exact
        // serialised shape, so we omit the key on the game path.
        const body = isAdmin
          ? { password, role: 'admin' as const }
          : { password };
        const res = await fetch(AUTH_URL, {
          method: 'POST',
          credentials: 'include',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify(body),
        });
        if (res.ok) {
          setStage('splash');
          return;
        }
        if (res.status === 401) {
          setError('Incorrect password — try again.');
          return;
        }
        setError('Unable to reach the server. Please try again.');
      } catch {
        setError('Unable to reach the server. Please try again.');
      }
    },
    [isAdmin],
  );

  const finishSplash = useCallback((): void => {
    setStage('game');
  }, []);

  return { stage, error, login, finishSplash };
}
